import React from 'react';
import { Smartphone, Download, Star, Zap, Code, Rocket, Users, CheckCircle, Apple, ArrowRight, ExternalLink } from 'lucide-react';

const MobileAppPage = () => {
  return (
    <div className="min-h-screen bg-black text-white pt-16">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="text-6xl mb-6">📱</div>
          <h1 className="text-4xl lg:text-5xl font-bold mb-6">
            <span className="text-white">TokenOS</span>
            <span className="bg-gradient-to-r from-purple-500 to-violet-500 bg-clip-text text-transparent"> Mobile App</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Build, deploy, and manage your Web3 projects on the go with the most powerful mobile development platform
          </p>
        </div>

        {/* App Preview */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-8">
            <h2 className="text-3xl font-bold text-white mb-6">Coming Soon to Your Phone</h2>
            <p className="text-gray-300 mb-8">
              The TokenOS mobile app brings the full power of AI-driven Web3 development 
              to your smartphone. Build smart contracts, deploy tokens, and manage projects 
              anywhere, anytime.
            </p>
            
            <div className="space-y-4 mb-8">
              <div className="flex items-center space-x-3">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span className="text-gray-300">Full AI code generation on mobile</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span className="text-gray-300">One-tap token deployment</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span className="text-gray-300">Real-time project monitoring</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span className="text-gray-300">Wallet integration & management</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span className="text-gray-300">Collaborative development tools</span>
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-900/50 to-violet-900/50 rounded-lg border border-purple-500/30 p-6">
              <h3 className="text-lg font-bold text-white mb-3">Early Access</h3>
              <p className="text-purple-300 text-sm mb-4">
                Join the beta program and be among the first to experience mobile Web3 development
              </p>
              <button className="w-full bg-gradient-to-r from-purple-500 to-violet-500 text-white py-3 rounded-lg hover:opacity-90 transition-opacity">
                Join Beta Waitlist
              </button>
            </div>
          </div>
          
          <div className="flex items-center justify-center">
            <div className="relative">
              <div className="w-80 h-[600px] bg-gray-900 rounded-[40px] border-8 border-gray-700 shadow-2xl">
                <div className="w-full h-full bg-black rounded-[32px] p-6 flex flex-col">
                  {/* Status Bar */}
                  <div className="flex justify-between items-center text-white text-xs mb-6">
                    <span>9:41</span>
                    <div className="flex space-x-1">
                      <div className="w-4 h-2 bg-white rounded-sm"></div>
                      <div className="w-1 h-2 bg-white rounded-sm"></div>
                    </div>
                  </div>
                  
                  {/* App Header */}
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-violet-500 rounded-2xl mx-auto mb-3 flex items-center justify-center">
                      <Code className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-white font-bold text-lg">TokenOS Mobile</h3>
                    <p className="text-gray-400 text-sm">AI-Powered Web3 Development</p>
                  </div>
                  
                  {/* Quick Actions */}
                  <div className="grid grid-cols-2 gap-3 mb-6">
                    <div className="bg-purple-600 rounded-xl p-4 text-center">
                      <Rocket className="h-6 w-6 text-white mx-auto mb-2" />
                      <span className="text-white text-xs">New Project</span>
                    </div>
                    <div className="bg-blue-600 rounded-xl p-4 text-center">
                      <Zap className="h-6 w-6 text-white mx-auto mb-2" />
                      <span className="text-white text-xs">Deploy Token</span>
                    </div>
                  </div>
                  
                  {/* Recent Projects */}
                  <div className="flex-1">
                    <h4 className="text-white font-semibold mb-3 text-sm">Recent Projects</h4>
                    <div className="space-y-2">
                      <div className="bg-gray-800 rounded-lg p-3">
                        <div className="flex justify-between items-center">
                          <span className="text-white text-xs">NFT Marketplace</span>
                          <span className="text-green-400 text-xs">Live</span>
                        </div>
                      </div>
                      <div className="bg-gray-800 rounded-lg p-3">
                        <div className="flex justify-between items-center">
                          <span className="text-white text-xs">DeFi Dashboard</span>
                          <span className="text-yellow-400 text-xs">Building</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">Powerful Features</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-6 text-center">
              <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Code className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">AI Code Editor</h3>
              <p className="text-gray-300 text-sm">
                Advanced mobile code editor with AI autocompletion, syntax highlighting, 
                and intelligent suggestions for Web3 development.
              </p>
            </div>
            
            <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-6 text-center">
              <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Rocket className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Instant Deployment</h3>
              <p className="text-gray-300 text-sm">
                Deploy smart contracts and launch tokens directly from your mobile device 
                with one-tap deployment to multiple networks.
              </p>
            </div>
            
            <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-6 text-center">
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Team Collaboration</h3>
              <p className="text-gray-300 text-sm">
                Collaborate with your team in real-time, share projects, and coordinate 
                development efforts seamlessly across devices.
              </p>
            </div>
          </div>
        </div>

        {/* Download Options */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">Download for Your Platform</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-2xl mx-auto">
            <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-8 text-center">
              <Apple className="h-16 w-16 text-white mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-3">iOS App</h3>
              <p className="text-gray-300 text-sm mb-6">
                Coming to the App Store with full iOS integration and native performance.
              </p>
              <button className="w-full bg-gray-700 text-gray-400 py-3 rounded-lg cursor-not-allowed">
                Coming Soon
              </button>
            </div>
            
            <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-8 text-center">
              <div className="h-16 w-16 mx-auto mb-4 flex items-center justify-center">
                <span className="text-4xl">🤖</span>
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Android App</h3>
              <p className="text-gray-300 text-sm mb-6">
                Available on Google Play with advanced Android features and customization.
              </p>
              <button className="w-full bg-gray-700 text-gray-400 py-3 rounded-lg cursor-not-allowed">
                Coming Soon
              </button>
            </div>
          </div>
        </div>

        {/* Beta Program */}
        <div className="mb-16">
          <div className="bg-gradient-to-r from-purple-900/50 to-violet-900/50 rounded-xl border border-purple-500/30 p-8">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-white mb-4">Join the Beta Program</h2>
              <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
                Be among the first to experience the future of mobile Web3 development. 
                Beta testers get exclusive access to new features and direct input on the app's development.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="text-center">
                  <Star className="h-8 w-8 text-yellow-400 mx-auto mb-2" />
                  <h3 className="font-semibold text-white mb-1">Early Access</h3>
                  <p className="text-gray-300 text-sm">Get the app before public release</p>
                </div>
                <div className="text-center">
                  <Users className="h-8 w-8 text-blue-400 mx-auto mb-2" />
                  <h3 className="font-semibold text-white mb-1">Direct Feedback</h3>
                  <p className="text-gray-300 text-sm">Shape the app's features and UI</p>
                </div>
                <div className="text-center">
                  <Zap className="h-8 w-8 text-purple-400 mx-auto mb-2" />
                  <h3 className="font-semibold text-white mb-1">Free Credits</h3>
                  <p className="text-gray-300 text-sm">Bonus credits for beta participation</p>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button className="px-8 py-4 bg-gradient-to-r from-purple-500 to-violet-500 text-white rounded-lg hover:opacity-90 transition-opacity">
                  Join iOS Beta
                </button>
                <button className="px-8 py-4 bg-gradient-to-r from-purple-500 to-violet-500 text-white rounded-lg hover:opacity-90 transition-opacity">
                  Join Android Beta
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">Frequently Asked Questions</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-6">
              <h3 className="text-lg font-semibold text-white mb-3">When will the app be available?</h3>
              <p className="text-gray-300 text-sm">
                The beta version is launching in Q2 2025, with the full public release planned for Q3 2025.
              </p>
            </div>
            
            <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-6">
              <h3 className="text-lg font-semibold text-white mb-3">Will it support all Web3 features?</h3>
              <p className="text-gray-300 text-sm">
                Yes, the mobile app will have feature parity with the web platform, including AI code generation and deployment.
              </p>
            </div>
            
            <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-6">
              <h3 className="text-lg font-semibold text-white mb-3">Is it free to use?</h3>
              <p className="text-gray-300 text-sm">
                The app is free to download. You'll need credits for AI features, just like the web platform.
              </p>
            </div>
            
            <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-6">
              <h3 className="text-lg font-semibold text-white mb-3">Which wallets are supported?</h3>
              <p className="text-gray-300 text-sm">
                All major Solana wallets will be supported, with WalletConnect integration for cross-chain compatibility.
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Stay Updated</h2>
          <p className="text-gray-300 mb-6">
            Get notified when the TokenOS mobile app launches and receive exclusive beta access
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/"
              className="px-6 py-3 bg-gradient-to-r from-purple-500 to-violet-500 text-white rounded-lg hover:opacity-90 transition-opacity inline-flex items-center justify-center"
            >
              <ArrowRight className="h-4 w-4 mr-2" />
              Try Web Version Now
            </a>
            <a
              href="https://t.me/tokenos_ai"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors inline-flex items-center justify-center"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Join Community
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileAppPage;